import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Row, Col, Typography, Tooltip, Popconfirm, Switch,
  InputNumber, TimePicker,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined,
  TeamOutlined, DollarOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { Amenity } from '../types';
import { staticApi } from '../utils/staticApi';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

const AMENITY_COLORS = [
  '#1890ff', '#52c41a', '#fa8c16', '#f5222d', '#722ed1',
  '#eb2f96', '#13c2c2', '#faad14', '#a0d911', '#2f54eb',
];

export const AmenityManagement: React.FC = () => {
  const {
    amenities,
    fetchAmenities,
    createAmenity,
    updateAmenity,
    deleteAmenity,
  } = useCondoStore();
  const { currentCondo } = useAuthStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentCondo?.id) fetchAmenities(currentCondo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchAmenities is a stable zustand action
  }, [currentCondo?.id]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    total:           amenities.length,
    requireApproval: amenities.filter(a => a.requiresApproval).length,
    paid:            amenities.filter(a => a.feePerHour > 0).length,
    free:            amenities.filter(a => a.feePerHour === 0).length,
  };

  // ── CRUD handlers ────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingAmenity(null);
    form.resetFields();
    form.setFieldsValue({
      requiresApproval: false,
      feePerHour: 0,
      capacity: 10,
      color: AMENITY_COLORS[0],
    });
    setIsModalOpen(true);
  };

  const openEdit = (amenity: Amenity) => {
    setEditingAmenity(amenity);
    form.setFieldsValue({
      name: amenity.name,
      location: amenity.location,
      capacity: amenity.capacity,
      requiresApproval: amenity.requiresApproval,
      feePerHour: amenity.feePerHour,
      description: amenity.description,
      availableFrom: amenity.availableFrom ? dayjs(amenity.availableFrom, 'HH:mm') : null,
      availableTo: amenity.availableTo ? dayjs(amenity.availableTo, 'HH:mm') : null,
      color: amenity.color ?? AMENITY_COLORS[0],
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      const payload = {
        ...vals,
        condoId: currentCondo!.id,
        availableFrom: vals.availableFrom ? vals.availableFrom.format('HH:mm') : '08:00',
        availableTo: vals.availableTo ? vals.availableTo.format('HH:mm') : '22:00',
      };

      if (editingAmenity) {
        await updateAmenity(editingAmenity.id, payload);
        staticApi.message.success('Amenity updated');
      } else {
        await createAmenity(payload);
        staticApi.message.success('Amenity created');
      }
      setIsModalOpen(false);
    } catch {
      // validation errors shown inline
    }
  };

  const handleDelete = async (id: string) => {
    await deleteAmenity(id);
    staticApi.message.success('Amenity deleted');
  };

  // ── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Amenity',
      key: 'amenity',
      render: (_: any, r: Amenity) => (
        <Space>
          <div style={{
            width: 12, height: 12, borderRadius: '50%',
            background: r.color ?? '#1890ff', display: 'inline-block',
          }} />
          <Text strong>{r.name}</Text>
        </Space>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Capacity',
      dataIndex: 'capacity',
      key: 'capacity',
      render: (v: number) => (
        <Space>
          <TeamOutlined />
          {v}
        </Space>
      ),
    },
    {
      title: 'Hours',
      key: 'hours',
      render: (_: any, r: Amenity) => (
        <Text>{r.availableFrom} – {r.availableTo}</Text>
      ),
    },
    {
      title: 'Fee / Hour',
      dataIndex: 'feePerHour',
      key: 'feePerHour',
      render: (v: number) => v > 0
        ? <Tag color="blue">${v.toFixed(2)}/hr</Tag>
        : <Tag color="green">Free</Tag>,
    },
    {
      title: 'Approval',
      dataIndex: 'requiresApproval',
      key: 'requiresApproval',
      render: (v: boolean) => v
        ? <Tag color="orange">Required</Tag>
        : <Tag color="green">Auto-Approve</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Amenity) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this amenity? Existing bookings may be affected."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <HomeOutlined style={{ marginRight: 8 }} />
            Amenity Management
          </Title>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Amenity
          </Button>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Amenities', value: stats.total, color: '#1890ff', bg: '#e6f7ff' },
          { label: 'Require Approval', value: stats.requireApproval, color: '#fa8c16', bg: '#fff7e6' },
          { label: 'Paid Amenities', value: stats.paid, color: '#722ed1', bg: '#f9f0ff' },
          { label: 'Free Amenities', value: stats.free, color: '#52c41a', bg: '#f6ffed' },
        ].map(c => (
          <Col span={6} key={c.label}>
            <Card style={{ background: c.bg, border: `1px solid ${c.color}33` }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: c.color }}>{c.value}</div>
                <div style={{ color: '#595959' }}>{c.label}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Amenity Cards Grid */}
      {amenities.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {amenities.map(a => (
            <Col span={8} key={a.id}>
              <Card
                hoverable
                style={{ borderTop: `4px solid ${a.color ?? '#1890ff'}` }}
                actions={[
                  <EditOutlined key="edit" onClick={() => openEdit(a)} />,
                  <Popconfirm
                    key="del"
                    title="Delete this amenity?"
                    onConfirm={() => handleDelete(a.id)}
                    okText="Delete"
                    okType="danger"
                  >
                    <DeleteOutlined style={{ color: '#f5222d' }} />
                  </Popconfirm>,
                ]}
              >
                <Text strong style={{ fontSize: 16 }}>{a.name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{a.location}</Text>
                <div style={{ marginTop: 8 }}>
                  <Space wrap>
                    <Tag icon={<TeamOutlined />}>{a.capacity} people</Tag>
                    <Tag icon={<ClockCircleOutlined />}>{a.availableFrom}–{a.availableTo}</Tag>
                    {a.feePerHour > 0
                      ? <Tag color="blue" icon={<DollarOutlined />}>${a.feePerHour}/hr</Tag>
                      : <Tag color="green">Free</Tag>}
                    {a.requiresApproval
                      ? <Tag color="orange">Approval Required</Tag>
                      : <Tag color="cyan">Auto-Approve</Tag>}
                  </Space>
                </div>
                {a.description && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    {a.description}
                  </Text>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Full Table */}
      <Card title="All Amenities">
        <Table
          dataSource={amenities}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingAmenity ? 'Edit Amenity' : 'Add New Amenity'}
        open={isModalOpen}
        onOk={handleSave}
        onCancel={() => setIsModalOpen(false)}
        okText="Save"
        width={640}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item name="name" label="Amenity Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Rooftop Terrace" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="location" label="Location" rules={[{ required: true }]}>
                <Input placeholder="e.g. Floor 12, Building A" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="capacity" label="Max Capacity" rules={[{ required: true }]}>
                <InputNumber min={1} max={500} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="feePerHour" label="Fee per Hour ($)" rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="requiresApproval" label="Requires Approval" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="availableFrom" label="Available From" rules={[{ required: true }]}>
                <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={30} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="availableTo" label="Available To" rules={[{ required: true }]}>
                <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={30} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Describe the amenity, rules, features..." />
          </Form.Item>
          <Form.Item name="color" label="Calendar Color">
            <Select>
              {AMENITY_COLORS.map(c => (
                <Option key={c} value={c}>
                  <Space>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: c, display: 'inline-block' }} />
                    {c}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
